import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Breadcrumbs, IconButton, Tooltip, Typography, makeStyles,
} from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import { useSelector } from 'react-redux';
import moment from 'moment';
import PageLayout from '../../PageLayout';
import DataTable from '../../DataTable/DataTable';
import { documentConstants } from '../../../constants';
import colors from '../../../assets/colors';
import DownloadDocument from '../../controls/DownloadDocument';

const useStyles = makeStyles(() => ({
  textBold: { fontWeight: 600 },
  text: {
    color: 'black',
    textDecoration: 'none',
    fontSize: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
  },
  internalText: {
    color: 'black',
    textDecoration: 'none',
    fontSize: '12px',
  },
  iconUpdate: { width: 16, height: 16, color: colors.orangeLight },
  iconButton: { textDecoration: 'none', padding: '0px', marginRight: '16px' },
  downloadIcon: {
    width: '16px',
    height: '16px',
    color: '#373435',
  },
}));

const DocumentsPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const userAuth = useSelector((state) => state.authentication.user);
  const isAdmin = userAuth.roles.includes('Admin');
  const title = intl.formatMessage({ id: 'document.documentsTitle', defaultMessage: 'Documentos' });
  const updateDocumentTitle = intl.formatMessage({ id: 'document.newVersion', defaultMessage: 'Crear nueva version' });

  function getEntityLabel(type) {
    if (type === documentConstants.PROCESS_DOCUMENT) return <FormattedMessage id="document.entityProcess" defaultMessage="Procesos" />;
    if (type === documentConstants.PERSON_DOCUMENT) return <FormattedMessage id="document.entityPerson" defaultMessage="Personas" />;
    if (type === documentConstants.DEVIATION_DOCUMENT) return <FormattedMessage id="document.entityDeviation" defaultMessage="Desvíos" />;
    if (type === documentConstants.TRAINING_DOCUMENT) return <FormattedMessage id="document.entityTraining" defaultMessage="Capacitaciones" />;
    if (type === documentConstants.PROVIDER_DOCUMENT) return <FormattedMessage id="document.entityProvider" defaultMessage="Proveedores" />;
    if (type === documentConstants.WORKFIELD_EVALUATION_DOCUMENT) return <FormattedMessage id="document.entityWorkFieldEvaluation" defaultMessage="Evaluaciones de rubro" />;
    return '-';
  }

  function getEntityUrl(type, id) {
    if (type === documentConstants.PROCESS_DOCUMENT) return `/processes/${id}`;
    if (type === documentConstants.PERSON_DOCUMENT) return `/people/${id}`;
    if (type === documentConstants.DEVIATION_DOCUMENT) return `/deviations/${id}`;
    if (type === documentConstants.TRAINING_DOCUMENT) return `/trainings/${id}`;
    if (type === documentConstants.PROVIDER_DOCUMENT) return `/providers/${id}`;
    if (type === documentConstants.WORKFIELD_EVALUATION_DOCUMENT) return `/workFieldEvaluations/${id}`;
    return '-';
  }

  function getProcessesFormatted(entity, type) {
    if (!entity.length || !type) {
      return '-';
    }
    if (entity.length === 1) {
      return (
        <Typography display="inline" className={classes.text} component={Link} to={getEntityUrl(type, entity[0].id)}>
          {entity[0].entityName}
        </Typography>
      );
    }
    if (entity.length === 2) {
      return (
        <Typography className={classes.text} style={{ maxWidth: 240 }}>
          <Typography display="inline" className={classes.internalText} component={Link} to={getEntityUrl(type, entity[0].id)}>
            {`${entity[0].entityName}, `}
          </Typography>
          <Typography display="inline" className={classes.internalText} component={Link} to={getEntityUrl(type, entity[1].id)}>
            {`${entity[1].entityName}`}
          </Typography>
        </Typography>
      );
    }

    return (
      <Typography className={classes.text} style={{ maxWidth: 240 }}>
        <Typography display="inline" className={classes.internalText} component={Link} to={getEntityUrl(type, entity[0].id)}>
          {`${entity[0].entityName}, `}
        </Typography>
        <Typography display="inline" className={classes.internalText} component={Link} to={getEntityUrl(type, entity[1].id)}>
          {`${entity[1].entityName}, `}
        </Typography>
        <FormattedMessage
          id="document.remainingEntities"
          defaultMessage="y varios más"
          values={{ count: (entity.length - 2) }}
        />
      </Typography>
    );
  }
  function actionsButtons(link, document) {
    if (!isAdmin) return null;
    return (
      <>
        <IconButton
          component={Link}
          to={link}
          className={classes.iconButton}
        >
          <Tooltip title={updateDocumentTitle}>
            <CreateIcon className={classes.iconUpdate} />
          </Tooltip>
        </IconButton>
        <DownloadDocument
          document={document}
          actualVersion={document.lastVersion}
          iconClassName={classes.downloadIcon}
        />
      </>
    );
  }

  const columns = [{
    name: 'id',
    options: {
      display: 'excluded',
      filter: false,
    },
  }, {
    name: 'title',
    label: intl.formatMessage({ id: 'document.name', defaultMessage: 'Nombre' }),
    options: {
      customBodyRender: (value, meta) => {
        const titleId = meta.rowData[0];
        return (
          <Typography className={`${classes.text} ${classes.textBold}`} component={Link} to={`/documents/${titleId}`}>
            {value}
          </Typography>
        );
      },
    },
  }, {
    name: 'type',
    label: intl.formatMessage({ id: 'document.entity', defaultMessage: 'Entidad' }),
    options: {
      display: 'excluded',
      customBodyRender: (value, meta) => {
        const id = meta.rowData[0];
        return (
          <Typography className={classes.text} component={Link} to={`/documents/${id}`}>
            {getEntityLabel(value)}
          </Typography>
        );
      },
      customDownloadRender: {
        renderValue: (value) => getEntityLabel(value),
      },
      filterOptions: {
        renderValue: (value) => getEntityLabel(value),
      },
      customFilterListOptions: {
        render: (value) => getEntityLabel(value),
      },
    },
  }, {
    name: 'relatedEntities',
    label: intl.formatMessage({ id: 'document.relatedEntities', defaultMessage: 'Entidades relacionadas' }),
    options: {
      filter: false,
      customBodyRender: (entity, meta) => getProcessesFormatted(entity, meta.rowData[2]),
    },
  }, {
    name: 'creatorUserNames',
    label: intl.formatMessage({ id: 'document.creatorUserNames', defaultMessage: 'Creador del documento' }),
  }, {
    name: 'lastVersion',
    label: intl.formatMessage({ id: 'document.lastVersionName', defaultMessage: 'Última versión' }),
    options: {
      customBodyRender: (value) => value.versionName,
    },
  }, {
    name: 'lastVersion',
    label: intl.formatMessage({ id: 'document.reviewerUserNames', defaultMessage: 'Revisor del documento' }),
    options: {
      customBodyRender: (value) => value.reviewerUserNames,
    },
  }, {
    name: 'lastVersion',
    label: intl.formatMessage({ id: 'document.lastVersionDate', defaultMessage: 'Fecha de última versión' }),
    options: {
      customBodyRender: (value) => {
        if (value.date === '-') return value.date;
        return moment(value.date).format('DD/MM/YYYY');
      },
    },
  }, {
    name: '',
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value, meta) => {
        const document = {
          id: meta.rowData[0],
          title: meta.rowData[1],
          lastVersion: meta.rowData[5],
        };
        const link = `/documents/${document.id}/new`;
        return actionsButtons(link, document);
      },
    },
  }];

  const keys = ['id', 'title', 'type', 'relatedEntities', 'creatorUserNames', 'lastVersion', 'lastVersion', 'lastVersion'];
  const fetchUrl = `companies/${userAuth.company.id}/Documents/`;
  const itemUrl = 'documents/';

  function DocumentsPageBreadcrumbs() {
    return (
      <Breadcrumbs separator="›" aria-label="breadcrumb">
        <Link color="inherit" to="/documents" style={{ textDecoration: 'none' }}>
          <Typography variant="caption" color="textPrimary" style={{ fontWeight: 700, fontSize: 11 }}>{title}</Typography>
        </Link>
      </Breadcrumbs>
    );
  }

  return (
    <PageLayout
      title={title}
      breadCrumbs={<DocumentsPageBreadcrumbs />}
    >
      <DataTable
        title={title}
        fetchUrl={fetchUrl}
        itemUrl={itemUrl}
        keys={keys}
        columns={columns}
        showFixedFilterForColumnIndex={2}
        customRenderForFixedFilterColumnIndex={getEntityLabel}
      />
    </PageLayout>
  );
};

export default DocumentsPage;
